import {render} from 'lit';
import {AjaxP, Module} from '@mamdev/cad.static-next-lib';
import {content} from './templates/content';
import type {MatchData, Match} from './IMatchData';

interface Selectors {
  wrapperOuter: string;
  gameList: string;
  gameScore: string;
  gameTime: string;
  teamName: string;
  navLeft: string;
  navRight: string;
}

export default class SportTickerTool extends Module {
  private readonly selectors: Selectors;
  private readonly slideIntervalDelay = 3000;
  private readonly slideIntervalRefresh = 60000;

  private jsonUrl: string;
  private sliderLength = 0;
  private sliderIntervalAutoslide: ReturnType<typeof setTimeout>;
  private sliderIntervalAutorefresh: ReturnType<typeof setTimeout>;

  /** Init. from DOM. */
  static attachTo(root: HTMLElement): SportTickerTool {
    return new SportTickerTool(root);
  }

  constructor(root: HTMLElement) {
    super(root);

    this.name_ = 'sporttickertool';

    this.selectors = {
      wrapperOuter: '.outer',
      gameList: '.js-game-list',
      gameScore: '.js-game-score',
      gameTime: '.js-game-time',
      teamName: '.js-teamname',
      navLeft: '.left',
      navRight: '.right'
    };
  }

  /** Starts the module */
  start() {
    const jsonUrl = this.root_.dataset.jsonUrl;

    if (jsonUrl) {
      this.jsonUrl = jsonUrl;
    } else {
      return;
    }

    void this.loadAndRenderJsonData();

    this.subscribe('window:resize', () => {
      this.setToolSize();
    });

    this.subscribe('layoutChanged', () => {
      this.setToolSize();
    });
  }

  /** Load JSON ticker data via AJAX, render markup from template and trigger full initialization. */
  async loadAndRenderJsonData() {
    try {
      const targetElement = this.root_.querySelector<HTMLElement>(this.selectors.gameList);

      if (targetElement) {
        const jsonData = await AjaxP.getJSON<MatchData>(this.jsonUrl);
        if (jsonData) {
          render(content(jsonData.matches), targetElement);
          this.setToolSize();
          this.initializeTool();
        }
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /** Calculate and set the tool size. */
  setToolSize() {
    try {
      // Remove slidesummy nodes if present
      this.removeSlidedummyNodes();

      // Setup selectors
      const wrapperOuter = this.root_.querySelector<HTMLElement>(this.selectors.wrapperOuter);
      const gameList = this.root_.querySelector<HTMLElement>(this.selectors.gameList);

      if (wrapperOuter && gameList) {
        const games = gameList.querySelectorAll<HTMLElement>('li');
        let totalGames = games.length || 1;
        this.sliderLength = totalGames;

        if (totalGames < 2) {
          // Less than 2 elements: is not a slider, but solo
          this.root_.classList.add('solo');
          this.root_.querySelector(this.selectors.navLeft)?.classList.add('none');
          this.root_.querySelector(this.selectors.navRight)?.classList.add('none');

          this.root_.getElementsByClassName('matchlink')[0].classList.add('inactive-link');
        } else {
          // Hide gamelist, get and set width, show gamelist.
          // This is mainly done so no css transition in shown upon viewport resizing.
          // gameList.style.display = 'none';
          wrapperOuter.style.width = '';
          const wrapperParent = wrapperOuter.parentElement;

          if (wrapperParent) {
            const parentWidth = wrapperParent.getBoundingClientRect().width;
            wrapperOuter.style.width = parentWidth + 'px';

            // Clone first game after last one if not already done.
            const firstGame = games[0].classList;
            const lastGame = games[totalGames - 1].classList;
            if (firstGame[0] !== lastGame[0]) {
              const gameClone = games[totalGames - 1].cloneNode(true) as HTMLElement;
              gameClone.style.width = parentWidth + 'px';
              gameList.insertBefore(gameClone, gameList.firstChild);
              totalGames++;
            }

            // Set width and left offset of gameList
            const parentPaddingLeft = 0;
            gameList.style.left = (parentWidth + parentPaddingLeft) * -1 + 'px';
            gameList.style.width = Math.round(totalGames * parentWidth + 0.5) + 'px';

            // Explicitly set width of each game
            games.forEach(game => {
              game.style.width = parentWidth + 'px';
            });
          }
        }

        this.setTeamNames();
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /** Initialize the tool: Size it, setup handlers, start interval. */
  initializeTool() {
    try {
      // Handle slider initialization
      if (this.sliderLength > 1) {
        // Handle mouse events (click left / right)
        this.setupMouseEventHandlers();

        // Start first time with autoslide
        this.slideIntervalControl('start');
      }

      // Initialize update interval
      this.sliderIntervalAutorefresh = setInterval(() => {
        this.updateSliderContents(true);
      }, this.slideIntervalRefresh);

      // Pubsub between sportticker and sporttickertool to keep them synchronous in conference and detail view.
      this.subscribe('sportticker:conferencevent', () => {
        // Clear interval and handle the refresh
        clearInterval(this.sliderIntervalAutorefresh);
        this.updateSliderContents(false);

        // Setup new interval and handle it
        this.sliderIntervalAutorefresh = setInterval(() => {
          this.updateSliderContents(true);
        }, this.slideIntervalRefresh);
      });
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /** Set up mouse click event handlers. */
  setupMouseEventHandlers() {
    this.root_.querySelector(this.selectors.navLeft)?.addEventListener('click', e => {
      this.handleMouseEvent(e, 'right');
    });

    this.root_.querySelector(this.selectors.navRight)?.addEventListener('click', e => {
      this.handleMouseEvent(e, 'left');
    });
  }

  handleMouseEvent(e: Event, direction: 'left' | 'right') {
    e.preventDefault();
    e.stopPropagation();

    // Stop slider interval
    this.slideIntervalControl('stop');

    // Slide
    this.slide(direction);

    // Re-start slider interval
    setTimeout(() => {
      this.slideIntervalControl('start');
    }, 10);
  }

  /** Set team names of all teams depending on tool width. */
  setTeamNames() {
    try {
      const toolWidth = parseInt(this.root_.dataset.toolWidth ?? '500');

      this.root_.querySelectorAll<HTMLElement>(this.selectors.teamName).forEach(teamnode => {
        let innerHTML: string;

        // Get team name text S/M/L according to tool width
        if (toolWidth < 350) {
          innerHTML = teamnode.dataset.textS ?? '';
        } else if (toolWidth < 500) {
          innerHTML = teamnode.dataset.textM ?? '';
        } else {
          innerHTML = teamnode.dataset.textL ?? '';
        }

        // If text is higehr than single line, use M
        if (teamnode.clientHeight >= 32) {
          innerHTML = teamnode.dataset.textM ?? '';
        }

        teamnode.innerHTML = innerHTML;
      });
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /** Update game data, i.e. update DOM contents and styles. */
  updateGameData(node: Element, matchData: Match) {
    if (node) {
      const gameScore = node.querySelector(this.selectors.gameScore);
      if (gameScore) {
        gameScore.innerHTML = matchData.score;
        gameScore.classList.remove('score', 'noscore');
        gameScore.classList.add(matchData.status === 'pending' ? 'noscore' : 'score');
      }

      const gameTime = node.querySelector(this.selectors.gameTime);
      if (gameTime) {
        gameTime.innerHTML = matchData.time;
        gameTime.classList.remove('live', 'date');
        gameTime.classList.add(matchData.status === 'live' ? 'live' : 'date');
      }
    }
  }

  /** Controller for the slide interval. */
  slideIntervalControl(action: 'start' | 'stop') {
    // Only needed for sliders, not single matches
    if (this.sliderLength === 1) {
      this.root_.getElementsByClassName('matchlink')[0].classList.add('inactive-link');
    } else if (this.sliderLength > 1) {
      if (action === 'stop') {
        clearInterval(this.sliderIntervalAutoslide);
      } else {
        this.sliderIntervalAutoslide = setInterval(() => {
          this.slide('left');
        }, this.slideIntervalDelay);
      }
    }
  }

  /** Handle the slide. Some DOM manipulation to create the "infinite" sliding. */
  slide(slideDirection: 'left' | 'right') {
    // ff slider consists of less than 2 elements, it's not a slider...
    if (this.sliderLength < 2) {
      return;
    }

    // Remove slidesummy nodes if present
    this.removeSlidedummyNodes();

    try {
      // Setup selectors
      const gameList = this.root_.querySelector(this.selectors.gameList);

      if (gameList) {
        const games = gameList.getElementsByTagName('li');
        const totalGames = games.length;
        let gameClone: HTMLLIElement;

        if (slideDirection === 'left') {
        // Slide left
        // Add 'slidedummy' class to first game
          games[0].classList.add('slidedummy');

          // Clone new first node
          gameClone = games[1].cloneNode(true) as HTMLLIElement;

          // Attach clone at the end
          gameList.appendChild(gameClone);
        } else if (slideDirection === 'right') {
        // Slide right
        // remove last node
          games[totalGames - 1].remove();

          // Clone new last node, add 'slidedummy' class, insert as new first node
          gameClone = games[totalGames - 2].cloneNode(true) as HTMLLIElement;
          /** @type Element */ (gameClone).classList.add('slidedummy');
          gameList.insertBefore(gameClone, gameList.firstChild);

          // remove 'slidedummy class'
          setTimeout(() => {
            gameClone.classList.remove('slidedummy');
          }, 30);
        }
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /**
   * Update the slider contents: get new data, stop the slider, update contents, restart slider.
   * @param {boolean} publishTickerEvent  Should the event be published towards mod-sportticker
   */
  updateSliderContents(publishTickerEvent: boolean) {
    AjaxP.getJSON<MatchData>(this.jsonUrl).then(jsonData => {
      if (jsonData) {
        // Stop the slider
        this.slideIntervalControl('stop');

        // Update slider contents
        const innerSliderDiv = this.root_.querySelector(this.selectors.gameList);
        if (innerSliderDiv) {
          jsonData.matches.forEach(match => {
            this.updateGameData(innerSliderDiv.getElementsByClassName('game' + match.index)[0], match);
          });
        }

        // Re-start the slider
        this.slideIntervalControl('start');

        // Optional: pubsub between sportticker and sporttickertool to keep them synchronous in conference and detail view.
        if (publishTickerEvent) {
          this.publish('sportticker:toolevent', {});
        }
      }
    }).catch(e => this.logUnknownError(e, true));
  }

  /** Helper function to remove slidedummy nodes if present. */
  removeSlidedummyNodes() {
    this.root_.querySelectorAll('.slidedummy').forEach(slidedummy => {
      slidedummy.remove();
    });
  }
}
